// src/data/productsData.ts

export interface Product {
  id: string;
  source_lang: string;
  name_source: string;
  description_source: string;
  manufacturer: string;
  type: string;
  image: string | null;
  name_en: string;
  name_de: string;
  name_es: string;
  description_en: string;
  description_de: string;
  description_es: string;
  applications_en?: string;
  applications_de?: string;
  applications_es?: string;
}

export interface FilterMetadata {
  manufacturers: string[];
  types: string[];
}

export interface InitialData {
  randomProducts: {
    products: Product[];
    totalProducts: number;
  };
  filterMetadata: FilterMetadata;
  // Set by api/get-initial-data.ts when it had to fall back to an empty
  // payload instead of a 500 (e.g. Google Sheets/Supabase were unreachable).
  // The HTTP response is still 200, so react-query treats it as a successful
  // fetch — this flag is how the UI tells "genuinely no products" apart from
  // "backend degraded" and can show a retry affordance instead of a bare
  // empty-catalog message.
  degraded?: boolean;
}

/**
 * Fetches the initial data bundle (random products + filter metadata) for the page.
 */
export const fetchInitialData = async (): Promise<InitialData> => {
  try {
    const response = await fetch('/api/get-initial-data');
    if (!response.ok) {
      const errorData = await response.json();
      console.error("API Error:", errorData.error);
      throw new Error(`Failed to fetch initial data. Status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching initial data from internal API:", error);
    throw error;
  }
};

/**
 * Loads paginated and filtered product data from our API endpoint.
 */
export const fetchProducts = async ({ 
  page = 1, 
  limit = 9,
  search = '',
  manufacturer = 'all',
  type = 'all',
  random = false,
}: {
  page?: number,
  limit?: number,
  search?: string,
  manufacturer?: string,
  type?: string,
  random?: boolean,
}): Promise<{ products: Product[], totalProducts: number }> => {
  try {
    // Construct the query parameters
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });
    if (search) params.append('search', search);
    if (manufacturer !== 'all') params.append('manufacturer', manufacturer);
    if (type !== 'all') params.append('type', type);
    if (random) params.append('random', 'true');

    const response = await fetch(`/api/get-products?${params.toString()}`);

    if (!response.ok) {
      const errorData = await response.json();
      console.error("API Error:", errorData.error);
      throw new Error(`Failed to fetch products. Status: ${response.status}`);
    }

    const data = await response.json();
    return data;

  } catch (error) {
    console.error("Error fetching products from internal API:", error);
    throw error;
  }
};

/**
 * Loads a single product by its ID from our API endpoint.
 * Note: This now requires a separate endpoint or modifying the main one if 'id' parameter is removed.
 * For now, we assume an endpoint that can handle an 'id' parameter exists.
 */
export const fetchProductById = async (productId: string): Promise<Product> => {
  try {
    // This part of the backend API was simplified. To restore this, 
    // the 'id' parameter handling needs to be re-added to 'get-products.ts'
    const response = await fetch(`/api/get-products?id=${productId}`);

    if (!response.ok) {
      const errorData = await response.json();
      console.error("API Error:", errorData.error);
      throw new Error(`Failed to fetch product with ID ${productId}. Status: ${response.status}`);
    }

    const data = await response.json();
    return data;

  } catch (error)
 {
    console.error(`Error fetching product with ID ${productId} from internal API:`, error);
    throw error;
  }
};